import { z } from "zod";

/**
 * @public — definition of an evaluation model.
 *
 * A model bundles everything that describes "how this organization scores
 * professional seniority": which axes exist, how many levels the rubric has,
 * the per-axis rubric (prose definitions), the signal-to-axis routing matrix,
 * and the per-axis / JD / requirement-match prompts. Each model is stored on
 * disk under `docs/labs/models/<id>/`:
 *
 * ```
 * docs/labs/models/<id>/
 * ├── model.json          # this schema serialized — axes, levels, metadata
 * ├── rubric/<axis>.md    # one markdown per axis, with L0..Lmax sections
 * ├── signal-matrix.json  # ModelSignalMatrix — feature → axis strengths
 * ├── prompts/
 * │   ├── axis-<axis>.md
 * │   ├── jd-extraction.md
 * │   └── requirement-match.md
 * └── calibration/        # gitignored — PII
 *     ├── candidates.jsonl
 *     ├── vagas.jsonl
 *     └── recomendacao.jsonl
 * ```
 *
 * The first model — `comp-universal` — is a seed shipped with the repo. Users
 * can edit it freely but cannot delete it (it backstops the empty-state).
 * Subsequent models are created by the user; the LLM bootstrap generates
 * their signal matrix + prompts from a user-written rubric.
 */

/** Slug regex for stable identifiers. Used on the model id, on each axis id,
 *  and on signal keys. Lowercase + underscores keep filesystem paths sane and
 *  let us reference them in prompts without quoting. */
export const MODEL_ID_REGEX = /^[a-z][a-z0-9-]*$/;
export const AXIS_ID_REGEX = /^[a-z][a-z0-9_]*$/;

// ── Structured competency-matrix fields (rater-training packet) ──────────────
// The matrix is authored as four "pillars" (see
// docs/labs/level-assessment/competency-matrix-fields.md and the companion
// competency-matrix-implementation-plan.md). Per-axis pillars (construct
// boundary, baseline, prestige traps, hard cap, cautionary profiles) ride the
// axis JSONB; per-cell / per-boundary pillars (what-counts, easy-to-miss,
// reference profiles, level boundaries) ride the rubric row's `fields` JSONB
// (`StructuredRubricFieldsSchema`). ALL of it is optional, so legacy models,
// persisted `ModelSnapshot`s, and the seed deserialize unchanged.

/** Pillar 4 · reference profile — a real example profile placed AT a level.
 *  `url` and `sampleId` are both optional: a pasted profile URL is the
 *  cold-start default (the reviewed corpus is empty at authoring time);
 *  `sampleId` links a reviewed `level_assessment_axis_samples` row once a
 *  corpus exists. */
export const ReferenceProfileSchema = z.object({
  url: z.string().optional(),
  sampleId: z.string().optional(),
  name: z.string().default(""),
  reason: z.string().default(""),
});
export type ReferenceProfile = z.infer<typeof ReferenceProfileSchema>;

/** Pillar 4 · cautionary profile — a commonly mis-judged profile: the wrong
 *  level a naive reader assigns, the correct level, and why. Becomes the single
 *  labelled "wrong" worked example in the scoring prompt. */
export const CautionaryProfileSchema = z.object({
  profile: z.string(),
  /** The level a naive reader would assign (the trap). */
  looksLevel: z.number().int().nonnegative(),
  /** The correct level. */
  actualLevel: z.number().int().nonnegative(),
  why: z.string().default(""),
});
export type CautionaryProfile = z.infer<typeof CautionaryProfileSchema>;

export const ModelAxisSchema = z.object({
  /** Stable slug (e.g. `influence`). Unique within a model. Used as the
   *  filesystem segment for rubric/prompt files, and as the key in signal
   *  routing and per-axis result entries. Renames must preserve the id so
   *  existing calibration rows stay valid. */
  id: z.string().regex(AXIS_ID_REGEX),
  /** Human label shown in UI (e.g. "Influence"). Editable freely. */
  label: z.string().min(1),
  /** Pillar 1 · "what it measures" — one-sentence description of the single
   *  construct this axis answers ("how broad is the reach of the work?").
   *  Surfaced in tooltips, becomes the scoring prompt's persona, and is fed to
   *  the bootstrap LLM as context. */
  description: z.string(),
  /** Importance weight of this axis in the weighted overall aggregation.
   *  Positive finite number; weights need NOT sum to 1 (they are normalized
   *  over the scored axes at aggregation time). Defaults to 1 so legacy
   *  persisted models (and flat-weight models) deserialize unchanged. */
  weight: z.number().positive().finite().default(1),
  /** Pillar 1 · "what it is not" — a short note on the construct boundary, to
   *  fight halo bleed into neighbouring axes and stop cross-routing of their
   *  evidence. Optional. */
  notAbout: z.string().optional(),
  /** Pillar 1 · baseline level — the lowest rung a normally-employed
   *  professional sits at on this axis. A level number, validated ⊆ the model
   *  (or discipline sub-band) range at the usecase boundary. Lets the scorer
   *  floor an ordinary professional instead of abstaining / under-scoring.
   *  `null`/absent for legacy + seed until enriched. */
  baseline: z.number().int().nonnegative().nullable().optional(),
  /** Pillar 3 · "what doesn't count" — cue-types that look like this axis but
   *  must not raise the level (prestige, senior-sounding title, brand-name
   *  employer). Drives structural prestige containment at the routing layer.
   *  Optional. */
  falsePositives: z.string().optional(),
  /** Pillar 3 · "hard cap" — a ceiling rule ("cannot exceed L4 without
   *  evidence of …"). Becomes an explicit ceiling in the scoring prompt.
   *  Optional. */
  hardCap: z.string().optional(),
  /** Pillar 4 · cautionary profiles — commonly mis-judged example profiles.
   *  Optional; empty for legacy + seed. */
  cautionaryProfiles: z.array(CautionaryProfileSchema).optional(),
});
export type ModelAxis = z.infer<typeof ModelAxisSchema>;

/** Pillar 2/3/4 · the structured per-cell fields for one (axis, level): the
 *  optional evidence model + reference profiles placed at that level. The level
 *  DESCRIPTION (Pillar 2) is NOT here — it stays in the rubric `content`
 *  markdown so the 3-parser contract + explainability path are untouched. */
export const StructuredRubricCellSchema = z.object({
  /** Pillar 3 · "what counts" — concrete CV/LinkedIn evidence at this level. */
  whatCounts: z.string().optional(),
  /** Pillar 3 · "what's easy to miss" — valid evidence in an unusual form. */
  easyToMiss: z.string().optional(),
  /** Pillar 4 · reference profiles placed at this level. */
  referenceProfiles: z.array(ReferenceProfileSchema).optional(),
});
export type StructuredRubricCell = z.infer<typeof StructuredRubricCellSchema>;

/** The structured per-cell / per-boundary fields for ONE axis, persisted in the
 *  nullable `fields` JSONB column of `level_assessment_model_rubrics` (parallel
 *  to the `content` markdown that still carries the level descriptions). All
 *  optional → a null column renders exactly like a legacy blob-only rubric.
 *
 *  - `cells` is keyed by level number as a string (`"4"`).
 *  - `boundaries` is keyed by the LOWER level of each adjacent pair (`"3"` =
 *    the L3→L4 crossing) and holds the Pillar-2 "crosses-when" discriminator. */
export const StructuredRubricFieldsSchema = z.object({
  cells: z.record(z.string(), StructuredRubricCellSchema).optional(),
  boundaries: z.record(z.string(), z.string()).optional(),
});
export type StructuredRubricFields = z.infer<typeof StructuredRubricFieldsSchema>;

/** The optional per-axis pillar fields an author fills, as a reusable Zod shape
 *  merged into each usecase's authored-axis input (create/update × model/
 *  discipline). Kept here so the four input schemas can't drift — they handle
 *  `id`/`label`/`description`/`weight` themselves and merge these five on top.
 *  Same fields `ModelAxisSchema` carries; see `buildAuthoredAxis` for how they
 *  become a persisted axis. */
export const AuthoredAxisPillarsSchema = z.object({
  notAbout: z.string().optional(),
  baseline: z.number().int().nonnegative().nullable().optional(),
  falsePositives: z.string().optional(),
  hardCap: z.string().optional(),
  cautionaryProfiles: z.array(CautionaryProfileSchema).optional(),
});

export const ModelLevelsSchema = z
  .object({
    /** Smallest level (typically 0). */
    min: z.number().int().min(0),
    /** Largest level (typically 7 in the seed). */
    max: z.number().int().min(0),
    /** Optional custom display name per level — model-wide, carried across
     *  every function/discipline (the same name set labels cross + vertical
     *  matrices). Keyed by the level number as a string (`{"5":"Senior"}`).
     *  Levels without a name fall back to "L{n}". Backward-compatible: old
     *  models/snapshots omit it. */
    names: z.record(z.string(), z.string()).optional(),
  })
  .refine((l) => l.max > l.min, { message: "levels.max must be greater than levels.min" });

/** A continuous sub-band of a model's level range — used by a discipline that
 *  spans only part of the full ladder (e.g. L3–L6 within an L0–L7 model). Unlike
 *  the model range it may be a single level (min === max). Bounds are validated
 *  ⊆ the model's range at the usecase boundary. */
export const LevelBandSchema = z
  .object({ min: z.number().int().min(0), max: z.number().int().min(0) })
  .refine((l) => l.max >= l.min, { message: "levels.max must be ≥ levels.min" });

/** Persisted in `signal-matrix.json` per model. The schema is intentionally
 *  open-ended on signal keys (string) so adding a new V2 feature doesn't
 *  require a model edit — unknown keys are silently absent from routing. */
export const SignalStrengthSchema = z.enum(["strong", "moderate", "weak"]);
export type SignalStrength = z.infer<typeof SignalStrengthSchema>;

export const ModelSignalRoutingSchema = z.object({
  /** Per-axis strength. Map keys are axis ids; values are the strength tier.
   *  An axis absent from this map is not informed by the signal. */
  axes: z.record(z.string(), SignalStrengthSchema).optional(),
  /** When true, this signal is a seniority prior passed to every axis call
   *  as shared context — it bounds the overall level (career length, lock-in)
   *  rather than belonging to any one axis. Mutually exclusive with `axes`. */
  prior: z.boolean().optional(),
});

/** One scope's routing — same shape as the legacy matrix; keys are the axis
 *  ids of that scope (cross axes, or one discipline's vertical axes). */
export const ScopedSignalMatrixSchema = z.object({
  /** Signal key → routing. Keys are extractor identifiers from V2
   *  (`cum_T1_years`, `pm_llm_has_director_or_vp_title`, ...). */
  signals: z.record(z.string(), ModelSignalRoutingSchema),
});
export type ScopedSignalMatrix = z.infer<typeof ScopedSignalMatrixSchema>;

/** Model-level matrix: the cross routing plus one routing per discipline id.
 *  A discipline absent from `disciplines` routes nothing for its vertical
 *  axes (callers fall back to an empty scoped matrix). */
export const ModelSignalMatrixSchema = z.object({
  cross: ScopedSignalMatrixSchema,
  disciplines: z.record(z.string(), ScopedSignalMatrixSchema).default({}),
});
export type ModelSignalMatrix = z.infer<typeof ModelSignalMatrixSchema>;

/** A discipline within a model — owns a set of **vertical** competencies
 *  (axes) scored only when a run targets this discipline. The model's
 *  `crossAxes` are scored for everyone; a discipline's `axes` are scored on
 *  top, for candidates evaluated against that discipline. */
export const DisciplineSchema = z.object({
  /** Slug, unique within the model. Key segment for the discipline's vertical
   *  rubrics + prompts (scope = this id) and the discipline's signal-matrix
   *  entry. */
  id: z.string().regex(MODEL_ID_REGEX),
  /** Human label shown in pickers/config (e.g. "Engenharia"). */
  label: z.string().min(1),
  /** One-paragraph description of the discipline's intent. */
  description: z.string().default(""),
  /** Display order in pickers/config. */
  order: z.number().int().default(0),
  /** Vertical competencies owned by this discipline. */
  axes: z.array(ModelAxisSchema).min(1),
  /** Optional continuous sub-band of the model's level range that this
   *  function actually spans (e.g. L3–L6 in an L0–L7 model). Absent = the full
   *  model range. Levels outside the band are auto-filled in the discipline's
   *  rubric with "(abaixo/acima da faixa desta função)" so the bootstrap and
   *  scoring prompts understand the boundary. Validated ⊆ the model's range at
   *  the usecase boundary. */
  levels: LevelBandSchema.optional(),
});
export type Discipline = z.infer<typeof DisciplineSchema>;

/** The serialized `model.json`. Prompts, rubric, signal matrix, and
 *  calibration live as sibling files — they're not embedded here both to
 *  keep model.json small and because each piece has its own edit workflow. */
export const LevelAssessmentModelSchema = z.object({
  /** Stable slug (`comp-universal`, `acme-engineering`, ...). The folder
   *  name on disk MUST match this id. */
  id: z.string().regex(MODEL_ID_REGEX),
  /** Display name shown in the model picker and config screens. */
  name: z.string().min(1),
  /** One-paragraph description of the model's intent. */
  description: z.string(),
  /** Model-wide ("cross") competencies, scored for every candidate
   *  regardless of discipline. Order is preserved in UI (axis cards, signal
   *  matrix columns, calibration form). */
  crossAxes: z.array(ModelAxisSchema).min(1),
  /** Discipline-scoped vertical competencies. Empty for a cross-only model
   *  (e.g. the migrated seed). */
  disciplines: z.array(DisciplineSchema).default([]),
  /** Inclusive level range. Used for clamping in the aggregator and for
   *  rendering "L0..Lmax" pickers. */
  levels: ModelLevelsSchema,
  /** Aggregation strategy. Only one option today; reserved for future
   *  variants (`min`, `weighted_mean`, ...). */
  aggregation: z.literal("mean_dispersion").default("mean_dispersion"),
  /** True for the shipped `comp-universal` seed. Seeds can be edited freely
   *  but cannot be deleted (the empty-state would be unrecoverable). */
  isSeed: z.boolean().default(false),
  /** ISO timestamp of when the rubric was last edited. The UI compares this
   *  with `lastBootstrapAt` to surface a "rubric changed since last
   *  bootstrap — re-bootstrap?" banner. */
  rubricUpdatedAt: z.string(),
  /** ISO timestamp of the last LLM bootstrap that wrote signal-matrix.json
   *  and the prompts. `null` when never bootstrapped (e.g. seed model whose
   *  prompts were hand-curated). */
  lastBootstrapAt: z.string().nullable(),
  /** ISO timestamp of when the model was created. */
  createdAt: z.string(),
});
export type LevelAssessmentModel = z.infer<typeof LevelAssessmentModelSchema>;

/** Snapshot of the model's identity embedded in each `LevelAssessmentResult`.
 *  Lets us render an archived result even after the source model was edited
 *  or deleted — the UI iterates over the snapshot, not the live model. */
export const ModelSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Cross competencies scored on this run. */
  crossAxes: z.array(ModelAxisSchema),
  /** The discipline the run was executed against — null for a cross-only run
   *  (model with no disciplines, e.g. migrated seed). */
  discipline: DisciplineSchema.nullable(),
  levels: ModelLevelsSchema,
});
export type ModelSnapshot = z.infer<typeof ModelSnapshotSchema>;

/** Stable id of the shipped seed model. Code that needs a default falls
 *  back to this — never to a hardcoded axis list. */
export const SEED_MODEL_ID = "comp-universal";

/** Build a `ModelSnapshot` for embedding in a result, pinning the discipline
 *  the run targeted (null for a cross-only run). */
export function snapshotModel(
  model: LevelAssessmentModel,
  disciplineId: string | null,
): ModelSnapshot {
  const discipline = disciplineId
    ? (model.disciplines.find((d) => d.id === disciplineId) ?? null)
    : null;
  return {
    id: model.id,
    name: model.name,
    crossAxes: model.crossAxes,
    discipline,
    levels: model.levels,
  };
}

/** All axes scored for a given run, tagged by scope. Helper shared by the
 *  scoring service and the aggregator: the cross axes (always) plus the
 *  selected discipline's vertical axes (when a discipline is targeted). */
export function axesForRun(model: LevelAssessmentModel, disciplineId: string | null) {
  const cross = model.crossAxes.map((a) => ({ ...a, scope: "cross" as const }));
  const discipline = disciplineId
    ? model.disciplines.find((d) => d.id === disciplineId)
    : undefined;
  const vertical = (discipline?.axes ?? []).map((a) => ({ ...a, scope: "vertical" as const }));
  return { cross, vertical };
}

/** True when `level` is in the model's allowed range. */
export function isValidLevel(model: LevelAssessmentModel | ModelSnapshot, level: number): boolean {
  if (!Number.isInteger(level)) return false;
  return level >= model.levels.min && level <= model.levels.max;
}

/** Position of a level relative to a discipline's level sub-band. `null` when
 *  the axis abstained or the discipline spans the full range (no sub-band) — in
 *  both cases there is no out-of-band state to report. */
export type BandStatus = "below" | "in" | "above";
export function bandStatusOf(
  level: number | null,
  band: { min: number; max: number } | null | undefined,
): BandStatus | null {
  if (level === null || !band) return null;
  if (level < band.min) return "below";
  if (level > band.max) return "above";
  return "in";
}

/** Collapse an out-of-band vertical level to a SINGLE rung just outside the
 *  discipline's sub-band — one level below the floor (`band.min − 1`) or one
 *  above the ceiling (`band.max + 1`), bounded by the model's range. In-band
 *  levels, abstentions (`null`), and axes with no sub-band pass through
 *  unchanged.
 *
 *  We deliberately do NOT estimate HOW far below/above the band a candidate
 *  sits: there are no behavioral anchors out-of-band, so any specific
 *  sub-floor level would be false precision. Representing a below-floor read as
 *  exactly `floor − 1` lets it drag the weighted overall by one rung (the
 *  genuine "below this function's ladder" deficiency) without fabricating a
 *  magnitude. The degenerate case where the band touches the model edge
 *  (`band.min === model.min`) leaves no rung below, so it clamps to the model
 *  floor — no drag is possible there. Symmetric at the ceiling. */
export function clampLevelToBandEdge(
  level: number | null,
  band: { min: number; max: number } | null | undefined,
  modelRange: { min: number; max: number },
): number | null {
  if (level === null || !band) return level;
  if (level < band.min) return Math.max(band.min - 1, modelRange.min);
  if (level > band.max) return Math.min(band.max + 1, modelRange.max);
  return level;
}

/** Outcome of bootstrapping one scope (cross, or one discipline). */
export interface ScopeBootstrapResult {
  /** Axes whose prompt write succeeded (by id). */
  okAxes: string[];
  /** Axes whose prompt step failed. */
  errorAxes: Record<string, string>;
  /** True when the scope's signal-matrix step succeeded. */
  matrixOk: boolean;
  /** Matrix error message when the matrix step failed. */
  matrixError: string | null;
}

/** Outcome of one bootstrap run — returned by both the server orchestrator
 *  and the create-with-bootstrap usecase. Per-scope: keyed by `"cross"` and
 *  each discipline id. Lives in shared so the client UI can render step status
 *  without pulling in server-only modules. */
export interface BootstrapResult {
  scopes: Record<string, ScopeBootstrapResult>;
}
